import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";

const HERO = "https://images.unsplash.com/photo-1724986481830-4e7b781c2bd1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBqZXdlbHJ5JTIwc3RvcmV8ZW58MHx8fHwxNzg2NDAzNTUwfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, ready, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ready && user) navigate(user.role === "admin" ? "/admin" : "/home", { replace: true });
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      navigate(u.role === "admin" ? "/admin" : "/home", { replace: true });
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-obsidian text-foreground">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={HERO} alt="ORSE GOLD" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute bottom-16 left-16 right-16">
          <img src="/orse-logo.png" alt="ORSE GOLD" className="h-16 w-auto object-contain drop-shadow-2xl" />
          <div className="text-sm tracking-luxe uppercase text-white/70 mt-5">Toptan Kuyumculuk · B2B Sipariş Sistemi</div>
          <p className="text-white/50 mt-6 max-w-md leading-relaxed">
            Koleksiyonları görüntüleyin, ürünleri seçin ve siparişinizi doğrudan ORSE GOLD'a iletin.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden text-center mb-10">
            <img src="/orse-logo.png" alt="ORSE GOLD" className="h-12 w-auto object-contain mx-auto" />
            <div className="text-[10px] tracking-luxe uppercase text-muted-foreground mt-3">Toptan Kuyumculuk</div>
          </div>

          <div className="flex items-center gap-2 text-muted-foreground mb-2">
            <Lock size={15} strokeWidth={1.5} />
            <span className="text-xs tracking-luxe uppercase">Yetkili Giriş</span>
          </div>
          <h1 className="font-display text-3xl text-foreground mb-1">Hoş Geldiniz</h1>
          <p className="text-sm text-muted-foreground mb-8">Hesabınıza giriş yaparak devam edin.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">E-posta</label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ornek@firma.com"
                required
                className="bg-surface border-border text-foreground h-11 focus-visible:ring-gold focus-visible:border-gold"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">Şifre</label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="bg-surface border-border text-foreground h-11 focus-visible:ring-gold focus-visible:border-gold"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-gold text-obsidian font-semibold rounded-md hover:bg-gold-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Giriş Yap"}
            </button>
          </form>

          <p className="text-xs text-muted-foreground mt-8 leading-relaxed">
            Bu sistem yalnızca ORSE GOLD'un yetkili toptan müşterilerine özeldir. Hesap açmak için ORSE GOLD ile iletişime geçin.
          </p>
        </div>
      </div>
    </div>
  );
}
